export const EMG_COST_NGN = 10000
export const EMG_COST_USD = 7
export const EMG_COST_KES = 870
export const EMG_COST = EMG_COST_NGN

/** Approximate NGN per 1 unit of currency, aligned with server billing FX defaults. */
export const NGN_PER_UNIT = {
  NGN: 1,
  USD: 1500,
  KES: 11.5,
  GHS: 95,
  ZAR: 82,
  EUR: 1620,
  GBP: 1900,
  UGX: 0.4,
  TZS: 0.58,
  RWF: 1.1,
  XOF: 2.5,
  XAF: 2.5,
  ZMW: 58,
  MWK: 0.87,
  EGP: 30,
}

export const CALCULATOR_CURRENCIES = [
  { id: "NGN", label: "NGN", hint: "Nigerian Naira", symbol: "₦", shortLabel: "₦ NGN" },
  { id: "USD", label: "USD", hint: "US Dollar", symbol: "$", shortLabel: "$ USD" },
  { id: "KES", label: "M-PESA", hint: "Kenyan Shilling via M-PESA", symbol: "KSh", shortLabel: "M-PESA" },
]

export const EMG_COST_BY_CURRENCY = {
  NGN: EMG_COST_NGN,
  USD: EMG_COST_USD,
  KES: EMG_COST_KES,
}

export const TOTAL_RECRUITER_TIME_ON_MANUAL_TASKS = 17.7
export const NUMBER_OF_WORKHOURS_PER_MONTH = 168
export const DEFAULT_AGENCY_FEE_PERCENTAGE = 20
export const MIN_AGENCY_FEE_PERCENTAGE = 15
export const MAX_AGENCY_FEE_PERCENTAGE = 25
export const BENCHMARK_SOURCE_URL = "https://formacv.ai/blog/recruiter-productivity-benchmarks"

const roundMoney = (value) => Math.round(Number(value) * 100) / 100

export const ngnPerUnit = (currency) => {
  const code = String(currency || "NGN").toUpperCase()
  return NGN_PER_UNIT[code] || NGN_PER_UNIT.USD
}

export const convertAmount = (amount, fromCurrency, toCurrency) => {
  const n = Number(amount)
  if (!Number.isFinite(n)) return n
  const from = String(fromCurrency || "NGN").toUpperCase()
  const to = String(toCurrency || "NGN").toUpperCase()
  if (from === to) return n
  return (n * ngnPerUnit(from)) / ngnPerUnit(to)
}

export const emgCostForCurrency = (currency) => {
  const code = String(currency || "NGN").toUpperCase()
  if (EMG_COST_BY_CURRENCY[code] != null) return EMG_COST_BY_CURRENCY[code]
  return roundMoney(EMG_COST_NGN / ngnPerUnit(code))
}

export const parseMoneyInput = (value) => {
  if (value === "" || value == null) {
    return { ok: false, reason: "empty" }
  }

  const n = typeof value === "number"
    ? value
    : Number(String(value).replace(/,/g, "").trim())

  if (!Number.isFinite(n) || n < 0) {
    return { ok: false, reason: "invalid" }
  }

  return { ok: true, value: n }
}

export const convertMoneyInput = (raw, fromCurrency, toCurrency) => {
  const parsed = parseMoneyInput(raw)
  if (!parsed.ok) return raw
  const converted = convertAmount(parsed.value, fromCurrency, toCurrency)
  if (!Number.isFinite(converted)) return raw
  return String(roundMoney(converted))
}

export const calculateAgencySavings = (
  annualSalaryOfVacancy,
  agencyFeePercentage = DEFAULT_AGENCY_FEE_PERCENTAGE,
  emgCost = EMG_COST
) => {
  const salary = parseMoneyInput(annualSalaryOfVacancy)
  if (!salary.ok) {
    return { status: salary.reason }
  }

  const feePercentage = Number(agencyFeePercentage)
  if (
    !Number.isFinite(feePercentage) ||
    feePercentage < MIN_AGENCY_FEE_PERCENTAGE ||
    feePercentage > MAX_AGENCY_FEE_PERCENTAGE
  ) {
    return { status: "invalid" }
  }

  const cost = Number(emgCost)
  const agencyFee = (salary.value * feePercentage) / 100
  const costSaving = agencyFee - cost
  const costReductionPercentage = agencyFee === 0
    ? null
    : (costSaving / agencyFee) * 100

  return {
    status: "ok",
    agencyFee,
    emgCost: cost,
    costSaving,
    costReductionPercentage,
    agencyFeePercentage: feePercentage,
  }
}

export const calculateInHouseSavings = (recruiterMonthlySalary, emgCost = EMG_COST) => {
  const salary = parseMoneyInput(recruiterMonthlySalary)
  if (!salary.ok) {
    return { status: salary.reason }
  }

  const cost = Number(emgCost)
  const recruiterHourlyCost = salary.value / NUMBER_OF_WORKHOURS_PER_MONTH
  const recruiterTimeCost = TOTAL_RECRUITER_TIME_ON_MANUAL_TASKS * recruiterHourlyCost
  const costSaving = recruiterTimeCost - cost
  const costReductionPercentage = recruiterTimeCost === 0
    ? null
    : (costSaving / recruiterTimeCost) * 100

  return {
    status: "ok",
    recruiterHourlyCost,
    recruiterTimeCost,
    emgCost: cost,
    costSaving,
    costReductionPercentage,
    recruiterTimeHours: TOTAL_RECRUITER_TIME_ON_MANUAL_TASKS,
  }
}

export const formatMoney = (value, currency = "NGN") => {
  if (!Number.isFinite(value)) return "—"
  const code = String(currency || "NGN").toUpperCase()
  try {
    const locales = {
      NGN: "en-NG",
      USD: "en-US",
      KES: "en-KE",
      GHS: "en-GH",
      ZAR: "en-ZA",
      EUR: "en-IE",
      GBP: "en-GB",
      UGX: "en-UG",
      TZS: "en-TZ",
      RWF: "en-RW",
      XOF: "fr-SN",
      XAF: "fr-CM",
      ZMW: "en-ZM",
      MWK: "en-MW",
      EGP: "en-EG",
    }
    return new Intl.NumberFormat(locales[code] || "en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  } catch {
    return `${code} ${value.toLocaleString("en", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }
}

export const formatNaira = (value) => formatMoney(value, "NGN")

export const formatPercentage = (value) => {
  if (!Number.isFinite(value)) return "—"
  return `${value.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`
}
